import 'firebase/compat/analytics';
import firebase from "firebase/compat/app";
import 'firebase/compat/auth';
import 'firebase/compat/firestore'
import { ArtistId } from "../models/Artist";
import { ArtistService } from "./ArtistService";
import ObserverService from "./ObserverService";
import { getFirestore } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { firebaseConfig, ENABLE_MAGIC_LINK } from '../config/env';

(window as any).authChangeWatcher = Function;

const MAGIC_LINK_EMAIL_KEY = 'magicLinkEmail';

export const app = firebase.initializeApp(firebaseConfig);

export const firestore=firebase.firestore(app)

// firebase.firestore().enablePersistence({ synchronizeTabs: true })
//   .catch(function (err) {
//     console.log(err);
//   });
export class LoginService {
  /**
   * Check what sign-in methods exist for an email.
   * Returns 'google' if the account uses Google OAuth,
   * 'email-link' if it uses magic link, or 'none' if new.
   */
  static checkExistingProvider = (email: string): Promise<'google' | 'email-link' | 'none'> => {
    // Note: fetchSignInMethodsForEmail returns empty arrays when email
    // enumeration protection is enabled (default for projects created
    // after Sept 2023). In that case we gracefully fall through to 'none'
    // and send a magic link. The ownerId migration in watchAuthState
    // handles any UID mismatches as a safety net.
    return firebase
      .auth()
      .fetchSignInMethodsForEmail(email)
      .then((methods) => {
        if (methods.includes('google.com')) {
          return 'google';
        }
        if (methods.includes('emailLink')) {
          return 'email-link';
        }
        return 'none';
      })
      .catch((err) => {
        console.warn('fetchSignInMethodsForEmail failed, falling through to magic link:', err);
        return 'none' as const;
      });
  };

  /**
   * Sign in with Google OAuth popup, scoped to a specific email via login_hint.
   */
  static loginWithGoogle = (email?: string) => {
    const provider = new firebase.auth.GoogleAuthProvider();
    provider.addScope("https://www.googleapis.com/auth/userinfo.profile");
    provider.addScope("https://www.googleapis.com/auth/userinfo.email");
    if (email) {
      provider.setCustomParameters({ login_hint: email });
    } else {
      provider.setCustomParameters({ prompt: 'select_account' });
    }
    return firebase.auth().signInWithPopup(provider);
  };

  /**
   * Send a magic link sign-in email to the user.
   * Returns a promise that resolves when the email is sent.
   */
  static sendMagicLink = (email: string): Promise<void> => {
    const actionCodeSettings: firebase.auth.ActionCodeSettings = {
      url: window.location.origin + '/login',
      handleCodeInApp: true,
    };
    return firebase
      .auth()
      .sendSignInLinkToEmail(email, actionCodeSettings)
      .then(() => {
        window.localStorage.setItem(MAGIC_LINK_EMAIL_KEY, email);
      });
  };

  /**
   * Check if the current URL is a magic link sign-in callback.
   */
  static isMagicLinkCallback = (): boolean => {
    return firebase.auth().isSignInWithEmailLink(window.location.href);
  };

  /**
   * Complete magic link sign-in using the link the user clicked.
   * If no email is provided, tries to retrieve it from localStorage.
   */
  static completeMagicLinkSignIn = (email?: string): Promise<firebase.auth.UserCredential> => {
    const storedEmail = email || window.localStorage.getItem(MAGIC_LINK_EMAIL_KEY);
    if (!storedEmail) {
      return Promise.reject(new Error('No email found. Please enter your email to complete sign-in.'));
    }
    return firebase
      .auth()
      .signInWithEmailLink(storedEmail, window.location.href)
      .then((result) => {
        window.localStorage.removeItem(MAGIC_LINK_EMAIL_KEY);
        // Clean the magic link params from the URL
        window.history.replaceState(null, '', '/login');
        return result;
      });
  };

  static login = () => {
    if (ENABLE_MAGIC_LINK) {
      window.location.href = '/login';
    } else {
      LoginService.loginWithGoogle().catch((err) => {
        if (err.code === 'auth/cancelled-popup-request' || err.code === 'auth/popup-closed-by-user') return;
        console.error("Google sign-in error:", err);
      });
    }
  };
  /**
   * Create the signed-in user's own profile, or migrate its ownerId to the
   * current uid. This used to be a client-side `changeProfile({ownerId})`
   * write, which let any account claim any profile; the rules now deny
   * that and the claimProfile Cloud Function derives the artist id from
   * the caller's verified email instead (security finding S02).
   */
  static claimProfile = (alias?: string) => {
    const claim = httpsCallable<{ alias?: string }, { artistId: string; created: boolean; migrated: boolean }>(
      getFunctions(),
      'claimProfile'
    );
    return claim(alias ? { alias } : {}).then((res) => res.data);
  };

  static logout = () => {
    return firebase.auth().signOut();
  };
  static watchAuthState = (
    onLoggedIn: (artistId: ArtistId, ownerId: string) => void,
    onLoggedOut: () => void
  ) => {
    const isObserving = ObserverService.please().exist("authentication");
    if (isObserving) {
      console.log("authstate observing already");
      return;
    } else {
      const authObserver = firebase.auth().onAuthStateChanged(function (user) {
        if (
          user &&
          user.providerData[0] &&
          user.providerData[0].email === null &&
          user.email !== null
        ) {
          user.providerData[0].email = user.email;
        }
        // Old accounts (pre-2021) can lack a top-level email on the Auth
        // record while the Google provider entry still carries it — fall
        // back so they don't get treated as logged out.
        const email = user?.email ?? user?.providerData?.[0]?.email;
        if (user && email) {
          firebase.analytics(app).setUserId(user.uid)
          // User is signed in.
          const artistId = ArtistService.calculateArtistId(email);

          const promptForNewAvatarAndLogin = () => {
            const alias = window.prompt(
              "Alias not found, what do we call you?"
            );
            if (alias) {
              LoginService.claimProfile(alias)
                .catch((e) => console.error("claimProfile failed", e))
                .finally(() => {
                  onLoggedIn(artistId, user.uid);
                });
            } else {
              onLoggedIn(artistId, user.uid);
            }
          };

          ArtistService.fetch(artistId)
            .catch(({message})=>{
              console.log(message);
              console.log(user);
              message&&message==='not-found'&&promptForNewAvatarAndLogin();
            })
            .then((artist) => {
              if (artist && typeof artist.alias === undefined) {
                promptForNewAvatarAndLogin();
              } else if (artist && artist.ownerId && artist.ownerId !== user.uid) {
                // ownerId mismatch — migrate to current Firebase UID.
                // Handles users whose UID changed when switching auth providers.
                console.log("Migrating ownerId from", artist.ownerId, "to", user.uid);
                LoginService.claimProfile()
                  .catch((e) => console.error("claimProfile (ownerId migration) failed", e))
                  .finally(() => {
                    onLoggedIn(artistId, user.uid);
                  });
              } else {
                onLoggedIn(artistId, user.uid);
              }
            });
        } else {
          onLoggedOut();
          firebase.analytics(app).setUserId("anon");
        }
      });
      ObserverService.please().add("authentication", authObserver);
    }
  };
}
